import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { formatTRY } from "@/lib/api";
import HotelLayout from "@/components/HotelLayout";
import Pagination from "@/components/Pagination";
import { Wallet, LoaderCircle, TrendingUp, CreditCard } from "lucide-react";

const PAGE_SIZE = 50;
const HOTEL_ROLES = ["otel_owner", "otel_manager", "otel_reception", "otel_accounting"];

const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; } };

const STATUS_LABELS = {
    paid: "Ödendi", initiated: "Başlatıldı", awaiting_transfer: "Transfer Bekliyor",
    partial: "Kısmi", at_hotel: "Otelde Ödenecek", pending: "Bekliyor",
    failed: "Başarısız", refunded: "İade",
};
const STATUS_COLOR = {
    paid: "bg-emerald-500", initiated: "bg-slate-400", awaiting_transfer: "bg-amber-500",
    partial: "bg-sky-500", at_hotel: "bg-purple-500", pending: "bg-amber-500",
    failed: "bg-rose-500", refunded: "bg-slate-500",
};

export default function HotelFinancePage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [payments, setPayments] = useState([]);
    const [paymentsTotal, setPaymentsTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, p] = await Promise.all([
                api.get("/hotel/finance/summary"),
                api.get("/hotel/finance/payments", { params: { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE } }),
            ]);
            setSummary(s.data);
            setPayments(p.data.items || []);
            setPaymentsTotal(p.data.total || 0);
        } finally { setLoading(false); }
    }, [page]);

    /* eslint-disable */
    useEffect(() => {
        if (authLoading) return;
        if (!user) return navigate("/h/login");
        if (!HOTEL_ROLES.includes(user.role)) return navigate("/admin");
        load();
    }, [user, authLoading, navigate, load]);
    /* eslint-enable */

    if (loading || !summary) {
        return <HotelLayout title="Finans"><div className="p-12 flex items-center justify-center"><LoaderCircle className="h-8 w-8 text-emerald-500 animate-spin" /></div></HotelLayout>;
    }

    const totals = summary.totals || {};
    const grandTotal = Object.values(totals).reduce((s, v) => s + (v.amount || 0), 0);
    const grandCount = Object.values(totals).reduce((s, v) => s + (v.count || 0), 0);
    const paidAmount = totals.paid?.amount || 0;
    const monthly = summary.monthly || [];
    const maxRev = Math.max(...monthly.map(m => m.revenue), 1);

    return (
        <HotelLayout title="Finans" hotelName={user?.full_name?.split(" ")[0]}>
            {/* Top KPI strip */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div data-testid="fin-total" className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white shadow-lg">
                    <Wallet className="h-5 w-5 opacity-80 mb-2" />
                    <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Toplam Rezervasyon Tutarı</div>
                    <div className="mt-1 text-2xl font-bold">{formatTRY(grandTotal)}</div>
                    <div className="text-xs opacity-90 mt-0.5">{grandCount} rezervasyon</div>
                </div>
                <div data-testid="fin-paid" className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800">
                    <CreditCard className="h-5 w-5 text-emerald-500 mb-2" />
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Tahsil Edilen</div>
                    <div className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{formatTRY(paidAmount)}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{totals.paid?.count || 0} ödeme</div>
                </div>
                <div data-testid="fin-pending" className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800">
                    <TrendingUp className="h-5 w-5 text-amber-500 mb-2" />
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Bekleyen Tahsilat</div>
                    <div className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{formatTRY(grandTotal - paidAmount)}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{grandCount - (totals.paid?.count || 0)} kayıt</div>
                </div>
            </div>

            {/* Payment status breakdown */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 mb-6">
                <h3 className="font-bold text-slate-900 dark:text-white mb-4">Ödeme Durumu Dağılımı</h3>
                <div className="space-y-2.5">
                    {Object.entries(totals).map(([status, v]) => {
                        const pct = grandTotal > 0 ? (v.amount / grandTotal * 100) : 0;
                        return (
                            <div key={status} data-testid={`fin-row-${status}`}>
                                <div className="flex items-center justify-between text-xs mb-1">
                                    <span className="font-semibold text-slate-700 dark:text-slate-300">{STATUS_LABELS[status] || status} <span className="opacity-60">({v.count})</span></span>
                                    <span className="font-bold text-slate-900 dark:text-white">{formatTRY(v.amount)}</span>
                                </div>
                                <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                    <div className={`h-full ${STATUS_COLOR[status] || "bg-slate-400"}`} style={{ width: `${pct}%` }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Monthly trend */}
            {monthly.length > 0 && (
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 mb-6">
                    <h3 className="font-bold text-slate-900 dark:text-white mb-4">Aylık Gelir Trendi (Son 12 Ay)</h3>
                    <div className="flex items-end gap-2 h-32">
                        {monthly.map((m) => (
                            <div key={m.month} data-testid={`fin-bar-${m.month}`} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${m.month}: ${formatTRY(m.revenue)} (${m.bookings} rez.)`}>
                                <div className="text-[9px] font-bold text-slate-600 dark:text-slate-400">{formatTRY(m.revenue)}</div>
                                <div className="w-full bg-gradient-to-t from-emerald-500 to-teal-400 rounded-t" style={{ height: `${(m.revenue / maxRev) * 100}%`, minHeight: 4 }} />
                                <div className="text-[10px] text-slate-500">{m.month.slice(5)}/{m.month.slice(2, 4)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Payments list */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
                    <h3 className="font-bold text-slate-900 dark:text-white">Tahsil Edilen Ödemeler ({paymentsTotal})</h3>
                </div>
                <div className="overflow-x-auto">
                    {payments.length === 0 ? (
                        <div className="p-12 text-center text-sm text-slate-500" data-testid="fin-payments-empty">Henüz tahsilat kaydı yok.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 dark:bg-slate-800/50">
                                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                                    <th className="px-4 py-3">Tarih</th>
                                    <th className="px-4 py-3">Misafir</th>
                                    <th className="px-4 py-3">Voucher</th>
                                    <th className="px-4 py-3">Yöntem</th>
                                    <th className="px-4 py-3 text-right">Tutar</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {payments.map((p) => (
                                    <tr key={p.payment_id} data-testid={`fin-payment-${p.payment_id}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                        <td className="px-4 py-3 text-slate-700">{fmtDate(p.date)}</td>
                                        <td className="px-4 py-3 font-semibold text-slate-900 dark:text-white">{p.customer_name || "—"}</td>
                                        <td className="px-4 py-3 text-xs font-mono text-slate-500">{p.voucher_no || p.booking_id?.slice(0, 8)}</td>
                                        <td className="px-4 py-3"><span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[10px] font-bold">{p.method || "—"}</span></td>
                                        <td className="px-4 py-3 text-right font-bold text-emerald-600 dark:text-emerald-400">{formatTRY(p.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <Pagination total={paymentsTotal} page={page} pageSize={PAGE_SIZE} onChange={setPage} label="ödeme" />
            </div>
        </HotelLayout>
    );
}
